import { useState, useEffect } from 'react';
import api from '../../services/api';

export default function AnalyticsPage() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<any>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/overview');
      setOverview(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!overview) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="text-5xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Yet</h3>
          <p className="text-gray-600 mb-4">Complete some tests to see your performance analytics</p>
          <button 
            onClick={() => window.location.href = '/tests'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Take a Test
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Performance Analytics</h1>
        <p className="text-gray-600 mt-2">Comprehensive insights into your learning journey</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
          <div className="text-sm font-medium opacity-90 mb-2">Tests Completed</div>
          <div className="text-4xl font-bold">{overview.totalTests || 0}</div>
          <div className="text-sm opacity-75 mt-2">Total attempts</div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-teal-600 rounded-lg shadow-lg p-6 text-white">
          <div className="text-sm font-medium opacity-90 mb-2">Average Score</div>
          <div className="text-4xl font-bold">{overview.averageScore || 0}%</div>
          <div className="text-sm opacity-75 mt-2">Across all tests</div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg shadow-lg p-6 text-white">
          <div className="text-sm font-medium opacity-90 mb-2">Questions Solved</div>
          <div className="text-4xl font-bold">{overview.totalQuestions || 0}</div>
          <div className="text-sm opacity-75 mt-2">Total answered</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-lg shadow-lg p-6 text-white">
          <div className="text-sm font-medium opacity-90 mb-2">Accuracy</div>
          <div className="text-4xl font-bold">{overview.accuracy || 0}%</div>
          <div className="text-sm opacity-75 mt-2">Overall accuracy</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Subject Performance</h2>
        <div className="space-y-4">
          {['Physics', 'Chemistry', 'Mathematics'].map((subject) => {
            const score = Math.floor(Math.random() * 40) + 60;
            return (
              <div key={subject}>
                <div className="flex justify-between mb-2">
                  <span className="font-medium text-gray-700">{subject}</span>
                  <span className="text-gray-600">{score}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full transition-all ${
                      score >= 80 ? 'bg-green-500' : 
                      score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${score}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Strengths</h3>
          <div className="space-y-3">
            {['Mechanics', 'Organic Chemistry', 'Calculus'].map((topic, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                <span className="text-2xl">✓</span>
                <div>
                  <div className="font-medium text-gray-900">{topic}</div>
                  <div className="text-sm text-gray-600">{85 + index * 2}% accuracy</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Areas to Improve</h3>
          <div className="space-y-3">
            {['Thermodynamics', 'Electrochemistry', 'Probability'].map((topic, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                <span className="text-2xl">⚠️</span>
                <div>
                  <div className="font-medium text-gray-900">{topic}</div>
                  <div className="text-sm text-gray-600">{45 + index * 3}% accuracy</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg shadow-lg p-8 text-white text-center">
        <div className="text-2xl font-bold mb-3">
          Keep practicing to improve your performance! 📈
        </div>
        <p className="text-lg opacity-90">
          Focus on weak areas and maintain consistency in strong subjects
        </p>
      </div>
    </div>
  );
}
